/**
 * WebSocket Cache Events Hook
 *
 * Connects to the admin cache events WebSocket for real-time cache invalidation.
 * When the backend invalidates cache keys, this hook broadcasts events to all
 * components that can update their data without full page reloads.
 *
 * Architecture:
 * - Backend: Redis cache with event-based invalidation (ADR-080)
 * - WebSocket: /admin/api/ws/cache-events sends invalidation events
 * - Frontend: Components subscribe to events and refetch only affected data
 *
 * Usage:
 *   const { isConnected, lastEvent, subscribe } = useCacheEvents();
 *
 *   useEffect(() => {
 *     const unsubscribe = subscribe('admin:users:*', () => {
 *       refetchUsers();
 *     });
 *     return unsubscribe;
 *   }, [subscribe]);
 *
 * Related:
 * - ADR-080: Admin Portal Redis Caching Layer
 * - backend/admin/api/cache_events.py
 * - backend/admin/core/cache_invalidation.py
 */

import { useEffect, useRef, useState, useCallback } from 'react';

// Build WebSocket URL from API URL
const getWsUrl = () => {
  const apiUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:4000';
  // Convert http to ws, https to wss
  const wsUrl = apiUrl.replace(/^http/, 'ws');
  // WebSocket is mounted under /admin/api (same as REST endpoints)
  return `${wsUrl}/admin/api/ws/cache-events`;
};

/**
 * WebSocket connection states
 */
const WS_STATE = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
};

/**
 * Hook for subscribing to cache invalidation events via WebSocket.
 *
 * @returns {Object} Hook state and methods
 * @property {string} connectionState - Current WebSocket connection state
 * @property {boolean} isConnected - Whether WebSocket is connected
 * @property {Object|null} lastEvent - Last received cache invalidation event
 * @property {Function} subscribe - Subscribe to cache key patterns
 */
export function useCacheEvents() {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const subscribersRef = useRef(new Map());

  const [connectionState, setConnectionState] = useState(WS_STATE.DISCONNECTED);
  const [lastEvent, setLastEvent] = useState(null);

  /**
   * Check if a cache key matches a pattern
   * Supports wildcards: admin:users:* matches admin:users:list:page=1
   */
  const matchesPattern = useCallback((key, pattern) => {
    if (pattern === '*') return true;
    if (pattern === key) return true;

    // Convert pattern to regex (simple wildcard support)
    const regexPattern = pattern
      .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape special chars
      .replace(/\*/g, '.*'); // Convert * to .*

    return new RegExp(`^${regexPattern}$`).test(key);
  }, []);

  /**
   * Notify all subscribers that match any of the invalidated keys
   */
  const notifySubscribers = useCallback((event) => {
    const { keys = [], event: eventType } = event;

    subscribersRef.current.forEach((callbacks, pattern) => {
      // Check if any invalidated key matches this pattern
      const matches = keys.some(key => matchesPattern(key, pattern));

      if (matches) {
        callbacks.forEach(callback => {
          try {
            callback(event);
          } catch (err) {
            console.error('[CacheEvents] Subscriber callback error:', err);
          }
        });
      }
    });

    // Also notify any full_invalidation subscribers
    if (eventType === 'full_invalidation') {
      subscribersRef.current.forEach((callbacks) => {
        callbacks.forEach(callback => {
          try {
            callback(event);
          } catch (err) {
            console.error('[CacheEvents] Subscriber callback error:', err);
          }
        });
      });
    }
  }, [matchesPattern]);

  /**
   * Subscribe to cache invalidation events for a key pattern
   *
   * @param {string} pattern - Cache key pattern (e.g., 'admin:users:*')
   * @param {Function} callback - Called when matching keys are invalidated
   * @returns {Function} Unsubscribe function
   */
  const subscribe = useCallback((pattern, callback) => {
    if (!subscribersRef.current.has(pattern)) {
      subscribersRef.current.set(pattern, new Set());
    }

    subscribersRef.current.get(pattern).add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = subscribersRef.current.get(pattern);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          subscribersRef.current.delete(pattern);
        }
      }
    };
  }, []);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      console.log('[CacheEvents] No auth token, skipping WebSocket connection');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[CacheEvents] Already connected');
      return;
    }

    setConnectionState(WS_STATE.CONNECTING);

    try {
      const wsUrl = `${getWsUrl()}?token=${encodeURIComponent(token)}`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('[CacheEvents] WebSocket connected');
        setConnectionState(WS_STATE.CONNECTED);

        // Start ping interval (every 30 seconds)
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 30000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          // Handle pong response
          if (event.data === 'pong') {
            return;
          }

          const data = JSON.parse(event.data);

          // Handle cache invalidation events
          if (data.type === 'cache_invalidation') {
            console.log('[CacheEvents] Cache invalidation:', data.event, data.keys);
            setLastEvent(data);
            notifySubscribers(data);
          } else if (data.type === 'connected') {
            console.log('[CacheEvents] Connection confirmed:', data.message);
          }
        } catch (err) {
          console.error('[CacheEvents] Failed to parse message:', err, event.data);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('[CacheEvents] WebSocket error:', error);
        setConnectionState(WS_STATE.ERROR);
      };

      wsRef.current.onclose = (event) => {
        console.log('[CacheEvents] WebSocket closed:', event.code, event.reason);
        setConnectionState(WS_STATE.DISCONNECTED);

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Reconnect after 5 seconds if not intentionally closed
        if (event.code !== 1000 && event.code !== 4001) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[CacheEvents] Attempting reconnect...');
            connect();
          }, 5000);
        }
      };
    } catch (err) {
      console.error('[CacheEvents] Failed to create WebSocket:', err);
      setConnectionState(WS_STATE.ERROR);
    }
  }, [notifySubscribers]);

  /**
   * Disconnect WebSocket
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    setConnectionState(WS_STATE.DISCONNECTED);
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Reconnect when token changes (login/logout)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'admin_token') {
        if (e.newValue) {
          // Token added - connect
          connect();
        } else {
          // Token removed - disconnect
          disconnect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, disconnect]);

  return {
    connectionState,
    isConnected: connectionState === WS_STATE.CONNECTED,
    lastEvent,
    subscribe,
    connect,
    disconnect,
  };
}

export default useCacheEvents;
